<template>
  <form class="guidedTourForm" @submit.prevent="">
    <Alert class="notBookable" v-if="!isBookable" :dismissable="false">{{
      $t('GUIDED_TOUR_IS_NOT_BOOKABLE')
    }}</Alert>

    <smoothReflow>
      <DatePicker
        v-show="showCalendar"
        class="datePicker"
        v-if="formDate !== '' && isAvailable"
        v-model="formDate"
        label="GUIDED_TOUR_SELECT_DATE"
        :availableDates="calendarDates"
        :soldOutDates="soldOutDates"
        :limits="calendarLimits"
        v-on:dateChange="updateCalendar"
      ></DatePicker>
    </smoothReflow>
    <div class="loadingContainer" v-if="isLoadingSingleCalendar">
      <Loading :visible="true" size="large"></Loading>
    </div>
    <smoothReflow>
      <div
        v-show="showCalendar"
        class="timePicker"
        v-if="
          formDate &&
          isAvailable &&
          !isSoldOut &&
          typeof calendarDatesAndTimes[formDate] !== 'undefined'
        "
      >
        <div>
          <label>{{ $t('GUIDED_TOUR_SELECT_TIME') }}</label>
        </div>
        <span v-for="(time, index) in calendarDatesAndTimes[formDate]" :key="time.time">
          <button
            class="timeBtn"
            type="button"
            @click="setTime(index, $event)"
            v-bind:class="{ selected: formatTime(time.time) === formTime }"
            :disabled="time.tickets == 0 || time.soldout"
            :title="soldOutTitle(time.soldout)"
          >
            {{ formatTime(time.time) }}
          </button>
        </span>
      </div>
    </smoothReflow>
    <Alert :condition="isSoldOut" class="guidedTourSoldOut">
      <smoothReflow>
        {{ $t('GUIDED_TOUR_SOLD_OUT') }}
      </smoothReflow>
    </Alert>
    <smoothReflow>
      <div
        class="info"
        v-if="
          (!isAvailable || !isAvailableToday) &&
          !isSoldOut &&
          nextAvailableDay &&
          !isLoadingSingleCalendar
        "
      >
        {{ $t('GUIDED_TOUR_NOT_AVAILABLE_THIS_DAY_BUT_AVAILABLE_ON') }}
        {{ formatDate(nextAvailableDay) }}.
        <button v-on:click="jumpToTheNextAvailableDay()">{{ $t('GUIDED_TOUR_SHOW_ME') }}</button>
      </div>
      <Alert
        v-else
        :condition="
          (!isAvailable || !isAvailableToday) &&
          !isSoldOut &&
          !isLoadingSingleCalendar &&
          !isBookableExternaly
        "
        class="guidedTourUnavailable"
      >
        <smoothReflow>
          {{ $t('GUIDED_TOUR_NOT_AVAILABLE') }}
        </smoothReflow>
      </Alert>
    </smoothReflow>
    <div
      v-if="(!isAvailable || isSoldOut || !isAvailableToday) && !isBookableExternaly"
      class="goBackNav"
    >
      <button type="button" @click="goBack()">{{ $t('NAV_BACK') }}</button>
    </div>
    <GuidedTourFormTour />
    <div
      v-if="
        tour.single_ticketing == 0 &&
        tour.book_complete_tour == 1 &&
        typeof formDate !== 'udefined' &&
        calendarDatesAndTimes[formDate] &&
        !isBookableExternaly
      "
    >
      <b>{{ form.tourCount }}</b> {{ $t('GUIDED_TOUR_SELECTED_TOUR_MESSAGE_1') }}
      <b>{{ maxToursForTime }}</b> {{ $t('GUIDED_TOUR_SELECTED_TOUR_MESSAGE_2') }}
      <b>{{ (calendarDatesAndTimes[formDate][formTime].tickets / maxToursForTime) * tourCount }}</b>
      {{ $t('GUIDED_TOUR_SELECTED_TOUR_MESSAGE_3') }}
    </div>
    <div class="availableTicketsInfo" v-else-if="areDateAndTimeSelected() && !isBookableExternaly">
      <span v-if="parseInt(tour.booking_without_tickets, 10) === 1">
        {{ $t('GUIDED_TOUR_AVAILABLE_PARTICIPANTS_FOR_SELECTED_TIME') }}:
      </span>
      <span v-else>
        <template v-if="!isSoldOut && isAvailableToday">
          {{ $t('GUIDED_TOUR_AVAILABLE_TICKETS_FOR_SELECTED_TIME') }}:
        </template>
      </span>
      <b v-if="typeof calendarDatesAndTimes[formDate] !== 'undefined'">
        {{ availableParticipants }}
      </b>
    </div>
    <GuidedTourFormTickets />
    <Alert :condition="!isFamilyTicketReady()">
      {{ $t('TICKETS_WRONG_FAMILY_TICKET_COUNT') }}
    </Alert>
    <Alert :condition="!isFamilyTicketReady()">
      {{ $t('TICKETS_WRONG_FAMILY_TICKET_COUNT') }}
    </Alert>
    <Alert :condition="overMaxParticipantsBookable">
      {{ $t('GUIDED_TOUR_OVERBOOKING_PARTICIPANTS_MSG_PART1') }}
      {{ tourCount }}
      {{ $t('GUIDED_TOUR_OVERBOOKING_PARTICIPANTS_MSG_PART2') }}
      {{ tour.max_participants_bookable }}
      {{ $t('GUIDED_TOUR_OVERBOOKING_PARTICIPANTS_MSG_PART3') }}
    </Alert>
    <div
      class="totalPrice"
      v-if="areDateAndTimeSelected() && !isSoldOut && isAvailableToday && !isBookableExternaly"
    >
      <div class="name">
        <b>{{ $t('GUIDED_TOUR_TOTAL_PRICE') }}</b>
        <span class="vatMessage">({{ $t('ALL_PRICES_INCLUDE_VAT') }})</span>
      </div>
      <div class="value totalPriceValue">{{ formatCurrency(totalPrice) }}</div>
    </div>

    <div class="addToCart" v-if="isBookableExternaly">
      <a class="btn btn-primary externalBookLink" :href="externalBookingLink" target="_blank">
        {{ tour.bookable_extern_text }}
        <Icon icon="externalLink" />
      </a>
    </div>
    <div class="addToCart" v-else-if="isAvailable && !isSoldOut && isAvailableToday">
      <button
        @click="add()"
        v-if="parseInt(tour.booking_without_tickets, 10) === 1"
        :disabled="
          !isReadyToCart() ||
          !areDateAndTimeSelected() ||
          !isFamilyTicketReady() ||
          isUnderBooking() ||
          overMaxParticipantsBookable ||
          !isBookable
        "
        type="submit"
        v-bind:class="{ hasTickets: isReadyToCart }"
      >
        {{ $t('GUIDED_TOUR_BUY') }}
        <span v-if="formDate">({{ formatDate(formDate) }})</span>
      </button>
      <button
        @click="add()"
        v-else
        :disabled="
          !isReadyToCart() ||
          !areDateAndTimeSelected() ||
          !isFamilyTicketReady() ||
          overMaxParticipantsBookable ||
          !isBookable
        "
        type="submit"
        v-bind:class="{ hasTickets: isReadyToCart() && ticketCount > 0 }"
      >
        {{ $t('GUIDED_TOUR_BUY_TICKETS_PREFIX') }} {{ ticketCount }}
        {{ $t('GUIDED_TOUR_BUY_TICKETS_SUFFIX') }}
        <span v-if="formDate">({{ formatDate(formDate) }})</span>
      </button>
    </div>
    <Questions
      v-if="questionsAreVisible"
      v-on:hideQuestions="hideQuestions()"
      v-on:done="add()"
    ></Questions>
    <teleport to="head">
      <template v-for="jsonld in jsonlds">
        <component :is="'script'" type="application/ld+json">
          {{ jsonld }}
        </component>
      </template>
    </teleport>
  </form>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex';
import moment from 'moment-mini';
import formater from '../../lib/formater';
import DatePicker from '../Form/DatePicker.vue';
import Loading from '../Loading.vue';
import GuidedTourFormTour from './GuidedTourFormTour.vue';
import GuidedTourFormTickets from './GuidedTourFormTickets.vue';
import Questions from './Questions.vue';
import Alert from '../Alert.vue';
import strip from '../../lib/stripHtml';
import Icon from '../Icon.vue';

let tryNextMonth = true;
export default {
  name: 'GuidedTourForm',
  components: {
    Alert,
    DatePicker,
    Loading,
    GuidedTourFormTour,
    GuidedTourFormTickets,
    Questions,
    Icon,
  },
  data() {
    return {
      showCalendar: false,
      month: 0,
      questionsAreVisible: false,
      tourId: undefined,
    };
  },
  computed: {
    ...mapState({
      language: (state) => state.language,
      currencySymbol: (state) => state.app.currencySymbol,
      tour: (state) => state.guidedTours.data.single,
      isLoadingSingleCalendar: (state) => state.guidedTours.isLoadingSingleCalendar,
      isLoadedSingleCalendar: (state) => state.guidedTours.isLoadedSingleCalendar,
      calendarDatesAndTimes: (state) => state.guidedTours.data.singleCalendar,
      calendarLimits: (state) => state.guidedTours.data.singleCalendarLimits,
      isLoadedSingleCalendarLimits: (state) => state.guidedTours.isLoadedSingleCalendarLimits,
      formTours: (state) => state.guidedTours.form.tours,
      formTickets: (state) => state.guidedTours.form.tickets,
      form: (state) => state.guidedTours.form,
      formTime: (state) => state.guidedTours.form.time,
      ticketCount: (state) => state.guidedTours.form.ticketCount,
      tourCount: (state) => state.guidedTours.form.tourCount,
      totalPrice: (state) => state.guidedTours.form.totalPrice,
      singleCalendar: (state) => state.guidedTours.data.singleCalendar,
      soldOutDates: (state) => state.guidedTours.data.soldOutDates,
      isLoadedSoldOutDates: (state) => state.guidedTours.isLoadedSoldOutDates,
      siteName: (state) => state.app.siteName,
      museumMainUrl: (state) => state.app.museumMainUrl,
    }),
    externalBookingLink() {
      let b = this.tour.bookable_extern_link;

      if (this.tour.bookable_extern_link.startsWith('mailto:')) {
        let subject = this.tour.name;

        if (this.form.date) {
          subject += ' - ' + this.formatDate(this.form.date);
        }

        if (this.form.date) {
          subject += ' ' + this.form.time + ' Uhr';
        }
        subject = encodeURIComponent(subject);

        b += `?subject=${subject}`;
      }

      return b;
    },
    jsonlds() {
      const occurrences = [];

      if (typeof this.tour.schedule !== 'undefined') {
        Object.keys(this.tour.schedule).forEach((k) => {
          const dateTime = this.tour.schedule[k].time.replace(' ', 'T');
          Object.keys(this.tour.priceList).forEach((k) => {
            const priceItem = this.tour.priceList[k];
            let availability = 'https://schema.org/InStock';

            const occurence = {
              '@context': 'https://schema.org/',
              '@type': 'Event',
              name: `${this.tour.name} - ${priceItem.name}`,
              image: this.tour.tourImage,
              description: strip(this.tour.description_shop_long),
              organizer: {
                '@type': 'Organization',
                name: this.siteName,
                url: this.museumMainUrl,
              },
              startDate: dateTime,
              //"endDate": "2025-07-21T23:00-05:00",
              eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
              location: {
                '@type': 'Place',
                name: this.siteName,
              },
              offers: {
                '@type': 'Offer',
                url: window.location,
                price: (priceItem.price / 100).toFixed(2),
                priceCurrency: this.currencySymbol,
                availability: availability,
                //"validFrom": "2024-05-21T12:00"
              },
            };
            occurrences.push(occurence);
          });
        });
        /*if(typeof this.ticketsForDate[0] !== 'undefined') {
          this.ticketsFoorDate.forEach(ticket => {

            Object.keys(this.timeslotsForDate).forEach(k => {
              const timeslot = this.timeslotsForDate[k]
              const start = timeslot.datetime.replace(" ", "T")
              let availability = "https://schema.org/InStock"

              const occurence = {
                '@context': 'https://schema.org/',
                '@type': 'Event',
                name: `${this.currentEvent.name} - ${ticket.name}`,
                image: this.currentEvent.event_image,
                description: strip(this.currentEvent.description),
                organizer: {
                  "@type": "Organization",
                  "name": this.siteName,
                  "url": this.museumMainUrl
                },
                "startDate": start,
                //"endDate": "2025-07-21T23:00-05:00",
                "eventAttendanceMode": "https://schema.org/OfflineEventAttendanceMode",
                "location": {
                  "@type": "Place",
                  "name": this.currentEvent.name,
                },
                "offers": {
                  "@type": "Offer",
                  "url": window.location,
                  "price": (ticket.price / 100).toFixed(2),
                  "priceCurrency": this.currencySymbol,
                  "availability": availability,
                  //"validFrom": "2024-05-21T12:00"
                },
              }
              occurrences.push(occurence)
            })
          })
        }*/
      }

      return occurrences;
    },
    formDate: {
      get() {
        return this.$store.state.guidedTours.form.date;
      },
      set(v) {
        this.setDay(v);
      },
    },
    availableParticipants() {
      let c = 0;
      c = this.calendarDatesAndTimes[this.formDate][this.formTime].tickets * this.tourCount;
      if (
        parseInt(this.tour.book_complete_tour, 10) === 0 &&
        parseInt(this.tour.single_ticketing, 10) === 1
      ) {
        c = this.calendarDatesAndTimes[this.formDate][this.formTime].tickets;
        return c;
      }
      if (
        parseInt(this.tour.book_complete_tour, 10) === 1 &&
        parseInt(this.tour.single_ticketing, 10) === 1
      ) {
        const ticketsPerTour =
          this.calendarDatesAndTimes[this.formDate][this.formTime].tickets /
          parseInt(this.calendarDatesAndTimes[this.formDate][this.formTime].tours, 10);
        c = `${this.calendarDatesAndTimes[this.formDate][this.formTime].tours} ${this.$t('GUIDED_TOUR_SELECTED_TOUR_MESSAGE_B1')} ${ticketsPerTour} ${this.$t('GUIDED_TOUR_SELECTED_TOUR_MESSAGE_B2')}`;
      }
      return c;
    },
    maxToursForTime() {
      let v = 0;
      if (typeof this.$store.state.guidedTours.form.time !== 'undefined') {
        if (typeof this.$store.state.guidedTours.data.single.schedule !== 'undefined') {
          if (
            typeof this.$store.state.guidedTours.data.single.schedule[
              this.$store.state.guidedTours.form.time
            ] !== 'undefined'
          ) {
            v = parseInt(
              this.$store.state.guidedTours.data.single.schedule[
                this.$store.state.guidedTours.form.time
              ].tours,
              10
            );
          }
        }
      }
      return v;
    },
    priceList() {
      if (typeof this.$store.state.guidedTours.form.time !== 'undefined') {
        if (typeof this.$store.state.guidedTours.data.single.schedule !== 'undefined') {
          if (
            typeof this.$store.state.guidedTours.data.single.schedule[
              this.$store.state.guidedTours.form.time
            ] !== 'undefined'
          ) {
            if (
              typeof this.$store.state.guidedTours.data.single.schedule[
                this.$store.state.guidedTours.form.time
              ].priceList !== 'undefined'
            ) {
              return this.$store.state.guidedTours.data.single.schedule[
                this.$store.state.guidedTours.form.time
              ].priceList;
            }
          }
        }
      }
      return this.$store.state.guidedTours.data.single.priceList;
    },
    isBookable() {
      if (parseInt(this.tour.isBookable, 10) === 0) {
        return false;
      }
      return true;
    },
    isBookableExternaly() {
      if (parseInt(this.tour.isBookable, 10) === -1) {
        return true;
      }
      return false;
    },
    overMaxParticipantsBookable() {
      return (
        this.tour.single_ticketing === '1' &&
        this.tour.book_complete_tour === '0' &&
        this.tourCount > parseInt(this.tour.max_participants_bookable, 10) &&
        parseInt(this.tour.max_participants_bookable, 10) !== 0
      );
    },
    calendarDates() {
      return Object.keys(this.singleCalendar);
    },
    ...mapGetters({
      isAvailable: 'guidedTours/isAvailable',
      isAvailableToday: 'guidedTours/isAvailableToday',
      nextAvailableDay: 'guidedTours/nextAvailableDay',
      isSoldOut: 'guidedTours/isSoldOut',
    }),
  },
  methods: {
    ...mapActions({
      getSingleCalendar: 'guidedTours/singleCalendar',
      getCalendarLimits: 'guidedTours/singleCalendarLimits',
      setFormDate: 'guidedTours/setFormDate',
      setFormTime: 'guidedTours/setFormTime',
      addToCart: 'guidedTours/addToCart',
      clearForm: 'guidedTours/clearForm',
      setFormTourCount: 'guidedTours/setFormTourCount',
    }),
    ...mapGetters({
      isLessTicketsThanTours: 'guidedTours/isLessTicketsThanTours',
      isMoreTicketsThanTours: 'guidedTours/isMoreTicketsThanTours',
    }),
    soldOutTitle(soldoutTime) {
      if (soldoutTime) {
        return this.$t('TIME_PICKER_SOLDOUT_TIME');
      }
      return '';
    },
    hideQuestions() {
      this.questionsAreVisible = false;
    },
    isUnderBooking() {
      if (
        parseInt(this.tour.book_complete_tour, 10) === 1 &&
        parseInt(this.tour.single_ticketing, 10) === 0
      ) {
        return false;
      }
      if (
        parseInt(this.tour.booking_without_tickets, 10) === 0 &&
        this.ticketCount < parseInt(this.tour.min_participants, 10)
      ) {
        return true;
      }
      if (
        parseInt(this.tour.booking_without_tickets, 10) === 1 &&
        this.tourCount < parseInt(this.tour.min_participants, 10)
      ) {
        return true;
      }

      return false;
    },
    goBack() {
      this.$router.go(-1);
    },
    formatCurrency(val) {
      return formater.currency(val);
    },
    formatPercentage(val) {
      return formater.percentage(val);
    },
    formatDate(val) {
      return formater.date(val);
    },
    getTicketCount(id) {
      return this.formTickets[id].count;
    },
    getTourCount(id) {
      if (typeof this.formTours[id] !== 'undefined') {
        return this.formTours[id].count;
      }
      return 0;
    },
    getCalendar(newDate, forceDate) {
      return new Promise((resolve, reject) => {
        const date = moment();
        if (typeof newDate !== 'undefined') {
          if (typeof newDate.month !== 'undefined') {
            date.month(newDate.month);
          }
          if (typeof newDate.year !== 'undefined') {
            date.year(newDate.year);
          }
          if (typeof newDate.day !== 'undefined') {
            let day = parseInt(newDate.day, 10);
            if (day === 0) {
              day = 1;
            }
            date.date(day);
          }
        }
        this.getSingleCalendar({ tour: this.$route.params.id, date, forceDate })
          .then(() => {
            // didn't found any date so looking at next month
            if (this.calendarDates.length === 0 && tryNextMonth) {
              date.add(1, 'month');
              const initialDay = date.date();
              date.date(1);
              this.getSingleCalendar({ tour: this.$route.params.id, date, forceDate }).then(() => {
                if (this.calendarDates.length === 0) {
                  // didn't find anything next month so going back to initial date
                  date.subtract(1, 'month');
                  date.date(initialDay);
                  this.getSingleCalendar({ tour: this.$route.params.id, date, forceDate });
                }
              });
            }
            tryNextMonth = false;
            resolve();
          })
          .catch(() => reject());
      });
    },
    updateCalendar(date) {
      this.getCalendar(date);
    },
    jumpToTheNextAvailableDay() {
      const d = this.nextAvailableDay.split('-');
      this.getCalendar({ year: d[0], month: d[1] - 1, day: d[2] });
    },
    setDay(date) {
      this.setFormDate(date);
    },
    setTime(time, event) {
      if (event) event.preventDefault();
      this.setFormTime(time);
    },
    formatTime(time) {
      return formater.time(time);
    },
    areDateAndTimeSelected() {
      if (typeof this.formTime !== 'undefined' && typeof this.formDate !== 'undefined') {
        return true;
      }
      return false;
    },
    isTicketCountMoreThanAvailableTickets() {
      if (typeof this.formDate === 'undefined' || typeof this.formTime === 'undefined') {
        return true;
      }
      if (
        this.ticketCount >
        parseInt(this.calendarDatesAndTimes[this.formDate][this.formTime].tickets, 10) *
          this.tourCount
      ) {
        return true;
      }
      if (parseInt(this.tour.booking_without_tickets, 10) === 1) {
        if (
          this.tourCount >
          parseInt(this.calendarDatesAndTimes[this.formDate][this.formTime].tickets, 10)
        ) {
          return true;
        }
      }
      return false;
    },
    isReadyToCart() {
      if (this.tourCount === 0) {
        return false;
      }
      if (
        parseInt(this.tour.booking_without_tickets, 10) === 1 &&
        this.tourCount > 0 &&
        !this.isTicketCountMoreThanAvailableTickets()
      ) {
        return true;
      }
      if (this.ticketCount < parseInt(this.tour.min_participants, 10)) {
        return false;
      }
      if (this.ticketCount > parseInt(this.tour.max_participants, 10) * this.form.tourCount) {
        return false;
      }
      if (this.isTicketCountMoreThanAvailableTickets()) {
        return false;
      }
      if (this.isLessTicketsThanTours()) {
        return false;
      }
      if (this.isMoreTicketsThanTours()) {
        return false;
      }
      return true;
    },
    add() {
      if (typeof this.tour.questions !== 'undefined') {
        if (this.tour.questions.length > 0 && !this.questionsAreVisible) {
          this.questionsAreVisible = true;
          return;
        }
      }
      this.addToCart().then(() => {
        this.clearForm();
        this.questionsAreVisible = false;
        this.$toasted.show(this.$t('GUIDED_TOUR_ADDED_TO_CART')).goAway(2000);
        this.$router.push({ name: 'checkout' });
      });
    },

    isFamilyTicketReady() {
      const familyTickets = [];
      if (typeof this.tour.tickets !== 'undefined') {
        if (
          typeof Object.keys(this.tour.tickets).length !== 'undefined' &&
          Object.keys(this.tour.tickets).length !== 0
        ) {
          Object.keys(this.tour.tickets).forEach((k) => {
            if (this.tour.tickets[k].family) {
              familyTickets.push(this.tour.tickets[k]);
            }
          });
        }
      }
      if (familyTickets.length === 0) {
        return true;
      }
      let returnVal = null;
      let emptyFamily = true;
      let wrongQuantities = false;
      familyTickets.forEach((ft) => {
        emptyFamily = true;
        const { id } = ft;
        wrongQuantities = false;
        if (returnVal !== false) {
          // each subticketId

          if (typeof this.form.tickets[id] !== 'undefined') {
            Object.keys(this.form.tickets[id].subTickets).forEach((subTicketId) => {
              const subTicket = this.form.tickets[id].subTickets[subTicketId];
              if (subTicket.count !== 0) {
                emptyFamily = false;
              }
            });
            if (!emptyFamily) {
              Object.keys(this.form.tickets[id].subTickets).forEach((subTicketId) => {
                const subTicket = this.form.tickets[id].subTickets[subTicketId];
                const subTicketData = ft.subtickets.find((st) => subTicketId === st.id);
                if (subTicket.count < parseInt(subTicketData.min_quantity, 10)) {
                  wrongQuantities = true;
                }
                if (subTicket.count > parseInt(subTicketData.max_quantity, 10)) {
                  wrongQuantities = true;
                }
              });
            }
          }
          if (emptyFamily) {
            returnVal = true;
          } else if (!wrongQuantities) {
            returnVal = true;
          } else if (wrongQuantities) {
            returnVal = false;
          }
        }
      });
      return returnVal;
    },
  },
  watch: {
    isLoadedSingleCalendar(v) {
      console.log('isLoadedSingleCalendar');
      if (v) {
        setTimeout(() => {
          this.showCalendar = true;
        }, 1000);
      }
    },
  },
  mounted() {
    this.tourId = this.$route.params.id;
    this.getCalendarLimits(this.tourId).then(() => {
      let d;
      if (this.calendarLimits.first !== null) {
        d = this.calendarLimits.first.split(' ')[0]; // eslint-disable-line
      }
      let forceDate = false;
      if (typeof this.$route.query.date !== 'undefined') {
        d = this.$route.query.date;
        forceDate = true;
      }
      this.setFormDate(d).then(() => {
        if (typeof d !== 'undefined') {
          const dArr = d.split('-');
          this.getCalendar({ year: dArr[0], month: dArr[1] - 1, day: dArr[2] }, forceDate)
            .then(() => {
              if (typeof this.$route.query.time !== 'undefined') {
                const t = this.$route.query.time.replace('-', ':');
                if (typeof this.singleCalendar[d][t] !== 'undefined') {
                  if (parseInt(this.singleCalendar[d][t].tickets, 10) !== 0) {
                    this.setFormTime(t);
                  }
                }
              }
            })
            .catch(() => {});
        } else {
          this.getCalendar()
            .then(() => {})
            .catch(() => {});
        }
      });
    });
  },
  beforeUnmount() {
    this.setFormDate(undefined);
  },
};
</script>

<style lang="scss" scoped>
@import '../../scss/grid';
@import '../../scss/buttons';

.guidedTourForm {
  margin-bottom: 2 * $sp;
}

.ticket,
.entryTicket {
  padding-top: $sp;
  padding-bottom: calc($sp / 2);
  @extend .row;
  margin-left: 0;
  margin-right: 0;
  .content {
    @extend .col-sm-7;
    @extend .col-xs-12;
    @media screen and (max-width: $screen-sm) {
      float: none;
    }
  }
  .formInputs,
  .values {
    max-width: 20em;

    @extend .col-sm-5;
    @extend .col-xs-10;
    @extend .col-xs-offset-1;
    @extend .col-sm-offset-0;
    @media screen and (max-width: $screen-sm) {
      margin-top: $sp;
      margin-left: auto;
      margin-right: auto;
      float: none;
    }
    .price {
      text-align: center;
    }
  }
}

.name,
.price {
  font-weight: 600;
}
.totalPrice {
  padding-top: $sp * 1;
  padding-left: $sp;
  padding-right: $sp;
  @extend .row;
  .name {
    @extend .col-xs-8;
    font-weight: 500;
  }
  .value {
    @extend .col-xs-4;
    text-align: right;
  }
}
.timePicker {
  margin-bottom: $sp;
}
.timeBtn {
  @extend .btn;
  @extend .btn-default;
  margin-right: calc($sp / 2);
  margin-bottom: calc($sp / 2);
  min-width: 4.5em;
}
.timeBtn.selected {
  @extend .btn-primary;
}
.availableTicketsInfo {
  margin-top: $sp;
}
.addToCart {
  margin-top: $sp * 2;
  text-align: center;
  button {
    @extend .btn;
    @extend .btn-primary;
    @extend .btn-lg;
  }
  .ticketTotal {
    display: none;
  }
}

@media screen and (max-width: $screen-xs) {
  .addToCart button {
    border: 0;
    opacity: 1;
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    border-radius: 0;
    width: 100%;
    z-index: 20;
    height: 62px;
    box-shadow: 0 0 4px 0 rgba(0, 0, 0, 0.5);
    transition: all 0.5s;
  }
  .addToCart button:not(.hasTickets) {
    background-color: darken($secondary-block-color-2, 30%) !important;
  }

  .addToCart .ticketTotal {
    display: inline-block;
    margin-left: 1em;
  }
}
.goBackNav {
  text-align: center;
}

.info {
  background-color: $secondary-block-color-1;
  padding: $sp;
  margin-bottom: $sp;
  button {
    margin: $sp;
  }
}
.loadingContainer {
  text-align: center;
  padding-top: $sp;
  padding-bottom: 3 * $sp;
}

.datePicker:deep(label) {
  font-size: 1.25em;
}
.notBookable {
  margin-top: $sp * 2;
  text-align: center;
}
.externalBookLink:deep(.icon) {
  margin-left: 0.5em;
}
.externalBookLink:deep(.fill) {
  fill: $btn-primary-color !important;
}
</style>
